import * as React from 'react'
import {
  EditModalActionBar,
  ConfirmDeleteModal,
  FormikSelect,
  FormikTextField,
} from 'components'
import {
  Box,
  Form,
  Accordion,
  Checkbox,
  PropTypes,
  Grid,
  Typography,
} from 'cs-kevlar-react'
import { Formik, FormikProps } from 'formik'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { EditDeviceForm } from './EditDeviceForm'
import {
  CustomerDevice,
  CustomerUser,
  IdKeyedObjectShape,
  User,
} from 'contexts/base'
import { DeviceProperty, CustomerDeviceLoggingFeatureTypes } from '../../api'
import * as Yup from 'yup'
import { useCurrentAgencyDevices } from 'hooks'

interface EditDeviceProps {
  reinitializeForm?: boolean
  device: CustomerDevice
  onClose: () => void
  onSave: (values: CustomerDevice) => Promise<void>
  onAdd?: (values: CustomerDevice) => Promise<void>
  onDelete?: () => Promise<void>
  users: CustomerUser[]
  children?: () => JSX.Element | null
  canDelete: boolean
  saveText: string
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  didActionComplete?: boolean
  isReplacingDevice?: boolean
  canEditDeviceCertificate: boolean
  canEditDeviceLogging: boolean
  agencyUsersLookup: IdKeyedObjectShape<User>
  licensedAgencyId?: string
}

interface VerboseLoggingValues {
  AvlLogging: boolean
  MediaLogging: boolean
  NebulaConnectionLogging: boolean
  PushNotificationLoggging: boolean
  NcicLogging: boolean
  RecordSearchLogging: boolean
}

interface VerboseLoggingOptions {
  AvlLogging: string
  MediaLogging: string
  NebulaConnectionLogging: string
  PushNotificationLoggging: string
  NcicLogging: string
  RecordSearchLogging: string
}

interface VerboseLoggingProps extends VerboseLoggingValues {
  EnableVerboseLogging: boolean
}

const verboseLoggingOptions: VerboseLoggingOptions = {
  AvlLogging: 'AVL / PLT',
  MediaLogging: 'Media',
  NebulaConnectionLogging: 'Nebula Connection',
  PushNotificationLoggging: 'Push Notifications',
  NcicLogging: 'State NCIC',
  RecordSearchLogging: 'Record Searching',
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      marginLeft: -2 * Spacing,
      marginRight: -2 * Spacing,
      flexDirection: 'column',
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    key: {
      color: theme.palette.neutralButton.main,
    },
    loggingOption: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
    container: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
    accordion: {
      '& .MuiAccordionDetails-root': {
        flexDirection: 'column',
      },
    },
    certStatus: {
      marginTop: theme.spacing(8),
    },
  }),
)

const booleanOptions: Option[] = [
  {
    key: 'true',
    value: true,
    label: 'true',
  },
  {
    key: 'false',
    value: false,
    label: 'false',
  },
]

export const EditDevice: React.FunctionComponent<EditDeviceProps> = ({
  reinitializeForm = false,
  device,
  onClose,
  onSave,
  onAdd,
  onDelete,
  users,
  children,
  canDelete,
  saveText,
  confirmColor = 'primary',
  canEditDeviceCertificate,
  canEditDeviceLogging,
  didActionComplete,
  isReplacingDevice,
  agencyUsersLookup,
  licensedAgencyId,
}) => {
  const classes = useStyles({})

  if (!licensedAgencyId) return null

  const { devices,
          getDevicePropertyLabelByEnum
        } = useCurrentAgencyDevices(licensedAgencyId)

  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(
    false,
  )

  const [verboseLoggingProps, setEnableVerboseLoggingProps] = React.useState<
    VerboseLoggingProps
  >({
    EnableVerboseLogging:
      device?.loggingConfiguration?.verboseLoggingEnabled ?? false,
    AvlLogging:
      device?.loggingConfiguration?.enabledTypes?.includes(
        CustomerDeviceLoggingFeatureTypes.AvlPlt,
      ) ?? false,
    MediaLogging:
      device?.loggingConfiguration?.enabledTypes?.includes(
        CustomerDeviceLoggingFeatureTypes.Media,
      ) ?? false,
    NebulaConnectionLogging:
      device?.loggingConfiguration?.enabledTypes?.includes(
        CustomerDeviceLoggingFeatureTypes.NebulaConnection,
      ) ?? false,
    PushNotificationLoggging:
      device?.loggingConfiguration?.enabledTypes?.includes(
        CustomerDeviceLoggingFeatureTypes.PushNotifications,
      ) ?? false,
    NcicLogging:
      device?.loggingConfiguration?.enabledTypes?.includes(
        CustomerDeviceLoggingFeatureTypes.StateNcic,
      ) ?? false,
    RecordSearchLogging:
      device?.loggingConfiguration?.enabledTypes?.includes(
        CustomerDeviceLoggingFeatureTypes.RecordSearching,
      ) ?? false,
  })
  const verboseLoggingPropsToEnumArray = (newProps: VerboseLoggingProps) => {
    const items = [
      newProps.AvlLogging && CustomerDeviceLoggingFeatureTypes.AvlPlt,
      newProps.MediaLogging && CustomerDeviceLoggingFeatureTypes.Media,
      newProps.NcicLogging && CustomerDeviceLoggingFeatureTypes.StateNcic,
      newProps.NebulaConnectionLogging &&
        CustomerDeviceLoggingFeatureTypes.NebulaConnection,
      newProps.PushNotificationLoggging &&
        CustomerDeviceLoggingFeatureTypes.PushNotifications,
      newProps.RecordSearchLogging &&
        CustomerDeviceLoggingFeatureTypes.RecordSearching,
    ]
      .filter((i) => !!i || i === 0)
      .map((i) => i as CustomerDeviceLoggingFeatureTypes)

    return items
  }

  const onEdit = async (values: CustomerDevice, actions: any) => {
    if (didActionComplete) {
      onClose()
      return
    }

    if (device.id === 'NEW') {
      await onAdd?.(values)
    } else {
      // return right away since onSave closes the action sheet
      onSave(values)
      return
    }

    actions.setSubmitting(false)
  }

  const onDeleteClick = () => {
    setIsConfirmingDelete(false)

    onDelete && onDelete()
  }

  const isAdding = device.id === 'NEW'
  const isEditing = device.id !== 'NEW' && !isReplacingDevice

  const deviceNameValidator = React.useMemo(() => {
    const deviceNames = new Set<string>()
    // ignore the current device name since we need to be able to save it with the current name
    devices
      .filter((item) => item.id !== device.id)
      .map((item) => deviceNames.add(item.name))

    return (name: string) => !deviceNames.has(name)
  }, [devices, device.id])

  const editDeviceFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .test(
          'Name is unique',
          'The device name must be unique.',
          deviceNameValidator,
        )
        .required('The Name field is required.'),
      agencyUserId: Yup.string(),
      notes: Yup.string().nullable(),
      certificateConfiguration: Yup.object().shape({
        allowManualCertificateRenewal: Yup.bool().typeError(
          'The value must be true or false',
        ),
        certificateRenewalWindowInSecondsOverride: Yup.number()
          .typeError('Must be a number')
          .nullable()
          .min(0, 'The value must not be negative.'),
      }),
    })
  }, [deviceNameValidator])

  const onDeviceLoggingCheckboxChanged = (
    key: keyof VerboseLoggingOptions,
    formikProps: FormikProps<CustomerDevice>,
  ) => {
    const newProps = {
      ...verboseLoggingProps,
      [key]: !verboseLoggingProps[key],
    }
    setEnableVerboseLoggingProps(newProps)

    formikProps.setValues(
      {
        ...formikProps.values,
        loggingConfiguration: {
          verboseLoggingEnabled: true,
          enabledTypes: verboseLoggingPropsToEnumArray(newProps),
        },
      },
      false,
    )

    formikProps.setFieldTouched('loggingConfiguration', true)
  }

  const toggleVerboseLogging = (formikProps: FormikProps<CustomerDevice>) => {
    const oldProps = verboseLoggingProps
    const loggingEnabled = !oldProps?.EnableVerboseLogging ?? false

    const newProps = {
      EnableVerboseLogging: loggingEnabled,
      AvlLogging: loggingEnabled ? oldProps.AvlLogging : false,
      MediaLogging: loggingEnabled ? oldProps.MediaLogging : false,
      NebulaConnectionLogging: loggingEnabled
        ? oldProps.NebulaConnectionLogging
        : false,
      PushNotificationLoggging: loggingEnabled
        ? oldProps.PushNotificationLoggging
        : false,
      NcicLogging: loggingEnabled ? oldProps.NcicLogging : false,
      RecordSearchLogging: loggingEnabled
        ? oldProps.RecordSearchLogging
        : false,
    }
    setEnableVerboseLoggingProps(newProps)

    formikProps.setValues(
      {
        ...formikProps.values,
        loggingConfiguration: {
          verboseLoggingEnabled: loggingEnabled,
          enabledTypes: verboseLoggingPropsToEnumArray(newProps),
        },
      },
      false,
    )

    formikProps.setFieldTouched('loggingConfiguration', true)
  }

  const deviceWithDefaults: CustomerDevice = {
    ...device,
    notes: device.notes === null ? '' : device.notes,
    assignedUserId: device.assignedUserId ?? '',
  }

  return (
    <React.Fragment>
      <Formik
        enableReinitialize={reinitializeForm}
        initialValues={deviceWithDefaults}
        validationSchema={editDeviceFormSchema}
        onSubmit={onEdit}
        validateOnBlur={saveText === 'Close' ? false : true}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Box className={classes.formFieldContainer}>
              <Accordion
                id="EditDevice::Details"
                title="Details"
                initiallyExpanded
              >
                <Grid container>
                  <EditDeviceForm
                    formikProps={formikProps}
                    users={users}
                    readOnly={!!didActionComplete}
                    agencyUsersLookup={agencyUsersLookup}
                    allowSendingActivationPin={(isAdding || !!isReplacingDevice) && !didActionComplete}
                  />
                </Grid>
              </Accordion>
              {isEditing && canEditDeviceLogging && (
                <Accordion
                  title="Logging Settings"
                  initiallyExpanded
                  id="EditDevice::LoggingSettings"
                >
                  <Grid container>
                    <Grid item xs={12}>
                      <Checkbox
                        label="Enable Verbose Logging"
                        id="enableVerboseLogging"
                        checked={verboseLoggingProps.EnableVerboseLogging}
                        onChange={() => toggleVerboseLogging(formikProps)}
                      />
                    </Grid>
                    {(verboseLoggingProps.EnableVerboseLogging ||
                      verboseLoggingProps.AvlLogging ||
                      verboseLoggingProps.MediaLogging ||
                      verboseLoggingProps.NcicLogging ||
                      verboseLoggingProps.NebulaConnectionLogging ||
                      verboseLoggingProps.PushNotificationLoggging ||
                      verboseLoggingProps.RecordSearchLogging) && (
                      <React.Fragment>
                        {Object.keys(verboseLoggingOptions).map((key) => {
                          const typedKey = key as keyof VerboseLoggingOptions
                          return (
                            <Grid
                              item
                              xs={12}
                              key={`VerboseLogging::${key}`}
                              className={classes.loggingOption}
                            >
                              <Checkbox
                                label={verboseLoggingOptions[typedKey]}
                                id={key}
                                checked={verboseLoggingProps[typedKey]}
                                onChange={() =>
                                  onDeviceLoggingCheckboxChanged(
                                    typedKey,
                                    formikProps,
                                  )
                                }
                              />
                            </Grid>
                          )
                        })}
                      </React.Fragment>
                    )}
                  </Grid>
                </Accordion>
              )}
              {isEditing && canEditDeviceCertificate && (
                <Accordion
                  title="Certificate Settings"
                  initiallyExpanded
                  id="EditDevice::CertificateSettings"
                >
                  <Grid container direction="column">
                    <Grid item>
                      <FormikSelect
                        formikProps={formikProps}
                        id="certificateConfiguration.allowManualCertificateRenewal"
                        fieldName="certificateConfiguration.allowManualCertificateRenewal"
                        label="Allow Manual Renewal"
                        options={booleanOptions}
                        fullWidth
                        disabled={!canEditDeviceCertificate}
                      />
                    </Grid>
                    <Grid item>
                      <FormikTextField
                        formikProps={formikProps}
                        id="certificateConfiguration.certificateRenewalWindowInSecondsOverride"
                        fieldName="certificateConfiguration.certificateRenewalWindowInSecondsOverride"
                        label="Renewal Window In Seconds Override"
                        type="number"
                        fullWidth
                        disabled={!canEditDeviceCertificate}
                      />
                    </Grid>
                  </Grid>
                </Accordion>
              )}
              {isEditing && (
                <Accordion
                  title="Additional Information"
                  initiallyExpanded
                  id="EditDevice::AdditionalInformation"
                >
                  <Grid container>
                    <Grid item xs={4}>
                      <Typography
                        variant="body2"
                        display="inline"
                        className={classes.key}
                      >
                        Device
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" display="inline">
                        {getDevicePropertyLabelByEnum(
                          device,
                          DeviceProperty.DeviceManufacturer,
                        )}{' '}
                        {getDevicePropertyLabelByEnum(
                          device,
                          DeviceProperty.DeviceModel,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body2"
                        display="inline"
                        className={classes.key}
                      >
                        OS Version
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" display="inline">
                        {getDevicePropertyLabelByEnum(
                          device,
                          DeviceProperty.OsVersion,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body2"
                        display="inline"
                        className={classes.key}
                      >
                        Field Ops Version
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" display="inline">
                        {getDevicePropertyLabelByEnum(
                          device,
                          DeviceProperty.AppVersion,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body2"
                        display="inline"
                        className={classes.key}
                      >
                        Device ID
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" display="inline">
                        {device.customerDeviceId}
                      </Typography>
                    </Grid>
                  </Grid>
                </Accordion>
              )}
              {isReplacingDevice && (
                <Box className={classes.container}>
                  <Typography color="secondary" variant="body2">
                    Replacing the current device will prohibit access to any
                    media items currently stored within FieldOps.
                  </Typography>
                </Box>
              )}
              <Box className={classes.container}>{children && children()}</Box>
            </Box>
            <Box className={classes.spacer} />
            <EditModalActionBar
              canDelete={canDelete}
              onDelete={() => setIsConfirmingDelete(true)}
              onCancel={onClose}
              onSave={saveText === 'Close' ? onClose : () => {}}
              formikProps={formikProps}
              saveText={saveText}
              saveColor={confirmColor}
              saveButtonType={saveText === 'Close' ? 'button' : 'submit'}
              canCancel={!didActionComplete}
              deleteText="Delete Device"
            />
          </Form>
        )}
      </Formik>
      {isEditing && (
        <ConfirmDeleteModal
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={onDeleteClick}
          open={isConfirmingDelete}
          line1Text="Deleting this device will prohibit access to any media items stored within Field Ops on this device or backed up to the cloud.  Please confirm you wish to delete this device.  This action cannot be reversed."
          line2Text={`Device: ${device.name}`}
          inputLabelText="Please enter DELETE to confirm deletion of this device."
        />
      )}
    </React.Fragment>
  )
}
